import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  ClassContentRecord,
  ClassContentDetail,
  PreviewInfo,
  CreateClassContentRequest,
  CreateClassContentResponseData,
  EditClassContentRequest,
  EditClassContentResponseData,
  DownloadLinkResponseData,
} from "../../types";
import {
  CreateClassContent,
  EditClassContent,
  SearchClassContent,
  GetClassContentById,
  GetPreviewLink,
  GetDownloadLink,
} from "../../services/endpoints/content.endpoint";
import { logout } from "./authSlice";
import type { RootState } from "../store";

export interface ContentState {
  records: ClassContentRecord[];
  selectedContent: ClassContentDetail | null;
  expandedContentId: string | null;
  previewInfo: PreviewInfo | null;
  createdContent: CreateClassContentResponseData | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ContentState = {
  records: [],
  selectedContent: null,
  expandedContentId: null,
  previewInfo: null,
  createdContent: null,
  isLoading: false,
  error: null,
};

export const searchContentThunk = createAsyncThunk<
  ClassContentRecord[],
  string | { query: string; force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "content/searchContent",
  (param, { rejectWithValue }) => {
    const selectQuery = typeof param === "string" ? param : param.query;
    return SearchClassContent({ selectQuery })
      .then((res) => res.data.records)
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: (param, { getState }) => {
      const { content } = getState();
      const force = typeof param === "object" && param.force;
      if (force) return true;
      return content.records.length === 0 && !content.isLoading && !content.error;
    },
  }
);

export const getContentByIdThunk = createAsyncThunk<
  ClassContentDetail,
  string,
  { state: RootState; rejectValue: string }
>(
  "content/getContentById",
  (recordId, { rejectWithValue }) => {
    return GetClassContentById({ module: "Class_Content", recordId })
      .then((res) => res.data)
      .catch((error: any) => rejectWithValue(error.message));
  }
);

export const createContentThunk = createAsyncThunk<
  CreateClassContentResponseData,
  CreateClassContentRequest,
  { state: RootState; rejectValue: string }
>(
  "content/createContent",
  (payload, { rejectWithValue }) => {
    return CreateClassContent(payload)
      .then((res) => res.data)
      .catch((error: any) => rejectWithValue(error.message));
  }
);

export const editContentThunk = createAsyncThunk<
  EditClassContentResponseData,
  EditClassContentRequest,
  { state: RootState; rejectValue: string }
>(
  "content/editContent",
  (payload, { rejectWithValue }) => {
    return EditClassContent(payload)
      .then((res) => res.data)
      .catch((error: any) => rejectWithValue(error.message));
  }
);

export const getPreviewLinkThunk = createAsyncThunk<
  PreviewInfo,
  string,
  { state: RootState; rejectValue: string }
>(
  "content/getPreviewLink",
  (resourceId, { rejectWithValue }) => {
    return GetPreviewLink({ resourceId })
      .then((res) => res.data.preview)
      .catch((error: any) => rejectWithValue(error.message));
  }
);

export const getDownloadLinkThunk = createAsyncThunk<
  DownloadLinkResponseData,
  string,
  { state: RootState; rejectValue: string }
>(
  "content/getDownloadLink",
  (resourceId, { rejectWithValue }) => {
    return GetDownloadLink({ resourceId })
      .then((res) => res.data)
      .catch((error: any) => rejectWithValue(error.message));
  }
);

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {
    setSelectedContent: (state, action: PayloadAction<ClassContentDetail | null>) => {
      state.selectedContent = action.payload;
    },
    setExpandedContentId: (state, action: PayloadAction<string | null>) => {
      state.expandedContentId = action.payload;
      if (action.payload === null) {
        state.selectedContent = null;
      }
    },
    clearPreviewInfo: (state) => {
      state.previewInfo = null;
    },
    clearContentState: (state) => {
      state.records = [];
      state.selectedContent = null;
      state.expandedContentId = null;
      state.previewInfo = null;
      state.createdContent = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search
      .addCase(searchContentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchContentThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
        state.error = null;
      })
      .addCase(searchContentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      // Get By Id
      .addCase(getContentByIdThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContentByIdThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedContent = action.payload;
        state.error = null;
      })
      .addCase(getContentByIdThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      // Create Content
      .addCase(createContentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createContentThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.createdContent = action.payload;
        state.error = null;
      })
      .addCase(createContentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      // Edit Content
      .addCase(editContentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editContentThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(editContentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      // Get Preview Link
      .addCase(getPreviewLinkThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPreviewLinkThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.previewInfo = action.payload;
        state.error = null;
      })
      .addCase(getPreviewLinkThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      // Logout reset
      .addCase(logout, (state) => {
        state.records = [];
        state.selectedContent = null;
        state.expandedContentId = null;
        state.previewInfo = null;
        state.createdContent = null;
        state.isLoading = false;
        state.error = null;
      });
  },
});

export const {
  setSelectedContent,
  setExpandedContentId,
  clearPreviewInfo,
  clearContentState,
} = contentSlice.actions;
export default contentSlice.reducer;
